/** @file Zod schema of the export options for the export tool/route, type-checked against @pose2d/exporter. */
import { z } from 'zod';
import type { ExportRequestOptions } from './exportTypes';
import { SCALE_MAX } from './scaleMax.data';

/** What `scale` means; an omitted scale stays omitted, so a sprite export can fit (R20-04). */
const SCALE = 'Sprite sheets: pixels per skeleton unit when given (1 included); omit it to fit every exported frame into frameW x frameH, and the reply reports the scale used: pass that value to every part of a split export. Rigs: the texture scale, at most 1 (default 1): pages hold every image at that fraction of its size and the skeleton is unchanged.';

/**
 * Annotated with the exporter's type (every option but `scale`, which may be omitted here): the build fails when the
 * two drift apart. runExport fills an omitted `scale` after deciding fit or not (R20-04).
 */
export const ExportOptionsSchema: z.ZodType<ExportRequestOptions> = z
  .object({
    target: z.enum(['rig', 'spritesheet']).default('rig'),
    maxPage: z.union([z.literal(1024), z.literal(2048), z.literal(4096)]).default(2048),
    scale: z.number().positive().max(SCALE_MAX).optional().describe(SCALE),
    padding: z.number().int().min(0).max(16).default(2),
    animations: z.array(z.string()).optional(),
    skins: z.array(z.string()).optional(),
    directions: z.array(z.string()).optional(),
    frameW: z.number().int().positive().max(4096).optional(),
    frameH: z.number().int().positive().max(4096).optional(),
    fps: z.number().positive().max(120).optional(),
    layout: z.enum(['grid', 'packed']).optional(),
  })
  .strict()
  .describe('Export options; for sprite sheets an omitted scale fits the frames and a given one, 1 included, is pixels per skeleton unit.');
